/**
 * StarSpinner — animated 5-point Lone Star loading indicator in antique brass.
 *
 * Rotates slowly with a gentle scale-pulse. Used site-wide anywhere a
 * "Loading…" state previously appeared. Two sizes: sm (18px) and md (32px).
 *
 * The SVG path is a classical 5-point star; the animation is defined once in
 * a <style> tag scoped by a unique className so multiple spinners can render
 * at once without conflict.
 */
export function StarSpinner({ size = "md", label = "Loading", className = "" }) {
  const dim = size === "sm" ? 18 : size === "lg" ? 44 : 32;
  return (
    <span
      role="status"
      aria-live="polite"
      aria-label={label}
      className={`inline-flex items-center gap-3 ${className}`}
      data-testid="star-spinner"
    >
      <svg
        width={dim}
        height={dim}
        viewBox="0 0 40 40"
        xmlns="http://www.w3.org/2000/svg"
        className="star-spinner-svg"
        aria-hidden="true"
      >
        <path
          d="M20 2.5 L24.9 14.7 L38.1 15.5 L27.9 24 L31.2 36.8 L20 29.6 L8.8 36.8 L12.1 24 L1.9 15.5 L15.1 14.7 Z"
          fill="#9D7E4C"
          stroke="#7A6338"
          strokeWidth="0.5"
          strokeLinejoin="round"
        />
      </svg>
      {label ? (
        <span className="text-xs uppercase tracking-[0.28em] text-slate2 font-cormorant italic normal-case tracking-normal">
          {label}
        </span>
      ) : null}
      <style>{`
        .star-spinner-svg {
          animation: starspin 2.4s ease-in-out infinite;
          transform-origin: 50% 50%;
          transform-box: fill-box;
          filter: drop-shadow(0 1px 0 rgba(157,126,76,0.15));
        }
        @keyframes starspin {
          0%   { transform: rotate(0deg)   scale(1);    opacity: 0.85; }
          40%  { transform: rotate(180deg) scale(1.08); opacity: 1;    }
          100% { transform: rotate(360deg) scale(1);    opacity: 0.85; }
        }
        @media (prefers-reduced-motion: reduce) {
          .star-spinner-svg { animation: starpulse 2s ease-in-out infinite; }
          @keyframes starpulse {
            0%, 100% { opacity: 0.6; }
            50%      { opacity: 1;   }
          }
        }
      `}</style>
    </span>
  );
}

export default StarSpinner;
